import React from 'react';
import { User } from '@supabase/supabase-js';
import { Contact } from '../../../types/Contact';
import { FileText, RotateCcw } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { updateContactCore } from '../../../lib/contactsCore';
import { addHistory, getHistory } from '../../../lib/history';

interface OverviewSectionProps {
    contact: Contact;
    user: User | null;
    onSave: () => void;
    tldrLoading: boolean;
    setTldrLoading: (loading: boolean) => void;
    formData: Contact;
    setFormData: React.Dispatch<React.SetStateAction<Contact>>;
    setHistory: React.Dispatch<React.SetStateAction<any[]>>;
    showNotification?: (message: string, type: 'success' | 'error') => void;
}

export default function OverviewSection({
    contact,
    user,
    onSave,
    tldrLoading,
    setTldrLoading,
    formData,
    setFormData,
    setHistory,
    showNotification
}: OverviewSectionProps) {
    const handleCopyTLDR = async () => {
        try {
            const text = (formData.tldr_summary || '').trim();
            if (!text) return;
            await navigator.clipboard.writeText(text);
            showNotification?.('TLDR copied to clipboard', 'success');
        } catch (e) {
            showNotification?.('Failed to copy TLDR', 'error');
        }
    };

    const handleGenerateTLDR = async () => {
        if (!user || !formData.id) return;
        setTldrLoading(true);
        try {
            const contactData = JSON.stringify(formData, null, 2);
            const prompt = `You are writing a very short TLDR about a person for a CRM. Requirements:\n- Output 1 to 3 short lines, no bullets.\n- Maximize memory triggers: role, company, location, standout facts.\n- Keep it concise and natural.\nHere is the contact data:\n${contactData}\nReturn ONLY the TLDR text.`;

            const { data, error } = await supabase.functions.invoke('gemini-ai', {
                body: { prompt }
            });
            if (error) throw error;

            let tldr = (data?.response || '').trim();
            if (!tldr) throw new Error('Empty TLDR');
            // Normalize to at most 3 lines
            const lines = tldr.split(/\r?\n/).filter(l => l.trim() !== '');
            if (lines.length > 3) {
                tldr = lines.slice(0, 3).join('\n');
            }

            // Save to DB via core
            await updateContactCore(supabase as any, String(formData.id), { tldr_summary: tldr } as any);

            setFormData(prev => ({ ...prev, tldr_summary: tldr }));
            showNotification?.(formData.tldr_summary ? 'Overview regenerated' : 'Overview generated', 'success');
            // Log history and refresh
            try {
                if (formData.id) {
                    await addHistory({ contact_id: formData.id, actor_id: user?.id || null, action: 'overview_generated', details: { length: tldr.length } });
                    const entries = await getHistory(formData.id);
                    setHistory(entries);
                }
            } catch { }
            onSave();
        } catch (e) {
            console.error('Overview generation failed:', e);
            showNotification?.('Failed to generate overview', 'error');
        } finally {
            setTldrLoading(false);
        }
    };

    return (
        <div className="bg-gray-900 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-white">Overview</h3>
                </div>
                <button
                    onClick={handleGenerateTLDR}
                    disabled={tldrLoading}
                    className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors text-sm disabled:opacity-50"
                >
                    <RotateCcw className={`w-4 h-4 ${tldrLoading ? 'animate-spin' : ''}`} />
                    <span>{formData.tldr_summary ? 'Regenerate' : 'Generate'}</span>
                </button>
            </div>

            {/* Overview Card without extra side padding wrapper */}
            <div
                className={`bg-gray-800 rounded-lg p-4 mb-1 ${formData.tldr_summary ? 'cursor-pointer hover:bg-gray-700/70 transition-colors' : ''}`}
                onClick={() => formData.tldr_summary && handleCopyTLDR()}
                role={formData.tldr_summary ? 'button' : undefined}
                aria-label={formData.tldr_summary ? 'Copy overview' : undefined}
                title={formData.tldr_summary ? 'Click to copy overview' : undefined}
            >
                {formData.tldr_summary ? (
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                        {formData.tldr_summary}
                    </p>
                ) : (
                    <p className="text-gray-500 text-sm italic">No overview yet. Click Generate to create one.</p>
                )}
            </div>
        </div>
    );
}